/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * Resolved-request preview for the authoring surfaces — R4a/R4e (#898).
 *
 * Refreshes on **Apply**, editor open, a context change, and any other **commit** —
 * §13 makes Apply the commit boundary for *a row*, but a paste, an add-recommended or
 * a raw-JSON edit commits many rows at once without any Apply, and those must resolve
 * too. See {@link RequestPreview.commit} for why they cannot simply call `refresh`.
 *
 * The load-bearing property is not the trigger, it is **staleness**. A preview
 * showing a value the current config would no longer produce is worse than no
 * preview: the operator checks it, believes it, and ships the other thing. So this
 * hook reports `stale` whenever the model has moved since the resolution it holds,
 * and callers must render stale rather than rendering the value.
 */

import React from 'react';
import { demandSourceService } from '../services/demand-source.service';
import type { DemandSource } from '../types/demand-source';
import {
  readAppliedTestContext,
  type AppliedTestContext,
  type TestRunContext,
} from '../services/demand-source.service';
import { readResolvedRequest, type ResolvedRequest } from '../components/shared/editors/resolved-request';
import type { DriverCapture } from '../components/shared/RequestResponseInspector';

export interface RequestPreview {
  /** The resolved request, or null when there is nothing to resolve yet. */
  resolved: ResolvedRequest | null;
  /**
   * The same resolution as the request it describes — URL, masked headers, body — for the test
   * panel's *Preview request* (`#979`). One resolve, two renderings: the rows read `resolved`, the
   * panel reads this, so the two cannot describe different requests.
   */
  capture: DriverCapture | null;
  /** What the resolve ran as: device, content, address and pod, as the server applied them. */
  applied: AppliedTestContext | null;
  /**
   * The held resolution no longer describes the current model, or one is in flight.
   * Callers render the mark as stale — never as a value (R4e).
   */
  stale: boolean;
  /** Resolve now. Call on Apply, on editor open, and on a context change. */
  refresh: () => void;
  /**
   * Mark the held resolution stale without resolving. Call when the model changes
   * between Applies, so the row stops claiming a value as soon as the value could
   * have changed rather than when we get around to asking.
   */
  invalidate: () => void;
  /**
   * The model just changed **and** that change is a commit — resolve it, once the
   * change has actually landed.
   *
   * Why this exists rather than callers doing `onChange(...); refresh()`: that
   * sequence resolves the model **as it was before the change**. `sourceRef` is
   * synced in an effect, so a `refresh()` in the same tick as the `onChange` reads
   * the previous closure — and worse, a child's effects run before its parent's, so
   * even deferring into a child effect is too early. `commit` routes through state
   * that this hook owns, so the resolve happens in a parent effect declared *after*
   * the sync, which is the only ordering that is correct.
   *
   * The observable failure without it: pasting a sample body applied ~20 mapped rows
   * and every one rendered `→ omitted`, because each looked itself up in the request
   * resolved *before* the paste and was legitimately absent from it. Editing any one
   * row Applied, which refreshed, which fixed all of them at once.
   */
  commit: () => void;
}

/**
 * @param getSource Builds the current form config. A getter rather than a value so
 *   the (non-trivial) payload assembly runs once per resolve instead of once per
 *   render, and so callers can define it after the hook's other dependencies.
 * @param enabled Skip entirely while the surface is not showing resolution.
 */
export function useRequestPreview(
  getSource: () => Partial<DemandSource> | null | undefined,
  /**
   * The surface's *Test as* choice (`useTestContext`), `{}` for the rig's defaults. Required: an
   * optional context is how both callers came to pass `undefined` while the test beside them ran as
   * the operator's choice (`#979`).
   */
  context: TestRunContext,
  enabled = true,
  /**
   * Changes when a structural change warrants re-resolving without an Apply —
   * typically `${endpoint}|${method}`. Those are edited on a different step, so
   * Apply never fires for them.
   */
  resolveKey?: string,
): RequestPreview {
  const [resolved, setResolved] = React.useState<ResolvedRequest | null>(null);
  const [capture, setCapture] = React.useState<DriverCapture | null>(null);
  const [applied, setApplied] = React.useState<AppliedTestContext | null>(null);
  /**
   * Starts **false**, not true. "We have not asked" is not "we are asking": a row
   * that renders `→ …` before any resolve has been attempted looks broken and stays
   * that way, which is exactly what shipped first. Nothing renders until a resolve
   * is genuinely in flight or has returned.
   */
  const [stale, setStale] = React.useState(false);

  // Held in refs so that typing does not re-create `refresh` and so nothing here
  // schedules a request — only an explicit `refresh` does. Synced in an effect
  // rather than during render: a ref written during render misbehaves under
  // concurrent rendering, and React's own lint rule says so.
  const sourceRef = React.useRef(getSource);
  const contextRef = React.useRef(context);
  React.useEffect(() => {
    sourceRef.current = getSource;
    contextRef.current = context;
  }, [getSource, context]);

  /**
   * Applies coalesce: five rows applied quickly is one useful answer, not five. A
   * request in flight is superseded rather than queued, and a superseded response is
   * discarded — otherwise a slow earlier resolve can land after a fast later one and
   * present an older config as current, which is the staleness bug wearing a
   * different hat.
   */
  const runIdRef = React.useRef(0);

  const refresh = React.useCallback(() => {
    if (!enabled) return;
    const current = sourceRef.current();
    if (!current?.endpoint) {
      // "Not yet", not an error. A source without an endpoint is an unfinished form,
      // and putting a warning on every row of one is noise (R4e).
      setResolved(null);
      setCapture(null);
      setApplied(null);
      setStale(false);
      return;
    }

    const runId = ++runIdRef.current;
    setStale(true);

    void demandSourceService
      .resolveRequestPreview(current, contextRef.current)
      .then((result) => {
        if (runId !== runIdRef.current) return;
        const held = ((result as { body?: { capture?: unknown } })?.body?.capture ?? null) as DriverCapture | null;
        setResolved(readResolvedRequest(held as never));
        setCapture(held?.request ? held : null);
        setApplied(readAppliedTestContext(result));
        setStale(false);
      })
      .catch(() => {
        if (runId !== runIdRef.current) return;
        // A failed resolve leaves no resolution rather than a wrong one. The row
        // shows nothing; it must never show the previous config's values as current.
        setResolved(null);
        setCapture(null);
        setApplied(null);
        setStale(false);
      });
  }, [enabled]);

  /**
   * Mark stale the moment the model changes, without resolving. This is what makes
   * the guarantee hold between an edit and the next Apply: the row stops claiming a
   * value as soon as the value could have changed, rather than when we get around to
   * asking.
   */
  const invalidate = React.useCallback(() => setStale(true), []);

  /**
   * Commit counter — bumped by {@link RequestPreview.commit}, resolved in the effect
   * below. The indirection is the point: see the note on `commit`.
   */
  const [commitNonce, setCommitNonce] = React.useState(0);
  const commit = React.useCallback(() => {
    setStale(true);
    setCommitNonce((n) => n + 1);
  }, []);

  React.useEffect(() => {
    // Declared **after** the `sourceRef` sync effect above, so by the time this runs
    // the ref points at a getter closed over the committed model. Reordering these
    // two effects reintroduces the resolve-the-previous-model bug silently.
    if (commitNonce > 0) refresh();
  }, [commitNonce, refresh]);

  /**
   * The other half of R4e's trigger: resolve on open, and again whenever the
   * endpoint or protocol changes. Apply covers row edits, but those are edited on a
   * different step and would otherwise never resolve at all — which is how every row
   * came to render a permanent `→ …`.
   */
  /**
   * **A context change is a resolve** (`#979`). The header promised it and the effects never did:
   * `refresh` reads the context through a ref, so a new *Test as* choice changed nothing until the
   * next Apply, and every row went on describing the previous device. Keyed by value, not by object
   * identity, so a caller rebuilding an equal context each render does not re-resolve.
   */
  const contextKey = `${context?.deviceType ?? ''}|${context?.contentId ?? ''}`;

  React.useEffect(() => {
    refresh();
  }, [refresh, resolveKey, contextKey]);

  return { resolved, capture, applied, stale, refresh, invalidate, commit };
}
